import { useMemo, useState } from 'react';
import { Search, XIcon, Check, Truck } from 'lucide-react';
import type { Job } from '../data/mockData';
import type { ApiStaff, ApiVehicle } from '../../lib/api';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

// Phase 31-01: the Status filter is the unified status (status + source +
// phaseStatus family) — same vocabulary as JobCard borders + JobDetailPanel
// pills since Phase 30-01. Non-Chargeable is intentionally absent from this
// list: Type=Ticket is the canonical filter for ticket-sourced jobs (Tom
// decision Q2=B).
export type StatusFilter =
  | 'provisional'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'postponed'
  | 'cancelled';

export type TypeFilter = 'deal' | 'ticket';

export interface BoardFilters {
  staffId: string | null;
  client: string | null;
  status: StatusFilter | null;
  type: TypeFilter | null;
  // Phase 37-01 (Issue #54): multi-select vehicle filter. Empty array = no
  // filter applied. OR-semantics across selected registrations: a job matches
  // when ANY of its assigned vehicles (across all displayed dates) is in the
  // selection.
  vehicleRegistrations: string[];
  searchTerm: string;
}

export const EMPTY_FILTERS: BoardFilters = {
  staffId: null,
  client: null,
  status: null,
  type: null,
  vehicleRegistrations: [],
  searchTerm: '',
};

// Pure helpers: derive whether a job matches a Status / Type filter value.
// Mirrors the unified-status logic in JobCard's activeCardBorderClass +
// JobDetailPanel's computeUnifiedStatus (Phase 30-01). Future extraction:
// consolidate the three derivations into a shared lib when next change
// touches any of them.
function matchesStatusFilter(job: Job, filter: StatusFilter): boolean {
  // 80-02 collapse: the board now distinguishes Provisional vs Confirmed off
  // the single phaseStatus lifecycle (was the deleted booking-status field). The
  // pre-unification split is preserved: provisional → Provisional; the
  // committed states confirmed/in_progress/completed → Confirmed; cancelled →
  // neither. in_progress/completed/cancelled also match their own filter.
  //
  // Issue #96 fix: a phased board card carries its OWN phase status — filter
  // by that, NOT the parent job's (a provisional job's PHASE-CANCELLED card
  // must not match "Provisional").
  if (job.phaseStatus) {
    if (filter === 'in_progress' || filter === 'completed' || filter === 'cancelled') {
      return job.phaseStatus === filter;
    }
    if (filter === 'postponed') return false; // postponement is job-level, never per-phase
    if (job.phaseStatus === 'cancelled') return false;
    // Provisional ↔ confirmed: the committed states (confirmed/in_progress/
    // completed) all read as "Confirmed".
    if (filter === 'provisional') return job.phaseStatus === 'provisional';
    return (
      job.phaseStatus === 'confirmed' ||
      job.phaseStatus === 'in_progress' ||
      job.phaseStatus === 'completed'
    );
  }
  // Non-phased jobs (synthetic tombstones / legacy rows without a phaseStatus):
  if (filter === 'in_progress') return job.status === 'in_progress';
  if (filter === 'completed') return job.status === 'completed';
  if (filter === 'postponed') return job.status === 'postponed';
  if (filter === 'cancelled') return job.status === 'cancelled';
  // Provisional / Confirmed only apply to scheduled deal/manual jobs.
  if (job.status !== 'scheduled') return false;
  if (job.source === 'hubspot_ticket') return false;
  if (filter === 'provisional') return job.phaseStatus === 'provisional';
  if (filter === 'confirmed')
    return (
      job.phaseStatus === 'confirmed' ||
      job.phaseStatus === 'in_progress' ||
      job.phaseStatus === 'completed'
    );
  return false;
}

function matchesTypeFilter(job: Job, filter: TypeFilter): boolean {
  const isTicket = job.source === 'hubspot_ticket';
  return filter === 'ticket' ? isTicket : !isTicket;
}

export function applyBoardFilters(
  jobs: Job[],
  filters: BoardFilters,
  staffNameMap: Map<string, string>,
  // Phase 37-01 (Issue #54): aggregated per-job registrations across all
  // dates (built by the parent from board.vehicles[]). Omitted = vehicle
  // filter is no-op (graceful degradation when vehicles data isn't loaded).
  vehicleRegsByJobId?: Map<string, Set<string>>,
): Job[] {
  const hasVehicleFilter = filters.vehicleRegistrations.length > 0;
  const hasDropdown = !!(
    filters.staffId ||
    filters.client ||
    filters.status ||
    filters.type ||
    hasVehicleFilter
  );
  const hasSearch = filters.searchTerm.length > 0;
  if (!hasDropdown && !hasSearch) return jobs;

  const term = hasSearch ? filters.searchTerm.toLowerCase() : '';

  return jobs.filter((job) => {
    // Dropdown filters (exact match + AND-combined)
    if (filters.staffId && !job.assignedStaff.some((a) => a.staffId === filters.staffId))
      return false;
    if (filters.client && job.client !== filters.client) return false;
    if (filters.status && !matchesStatusFilter(job, filters.status)) return false;
    if (filters.type && !matchesTypeFilter(job, filters.type)) return false;
    // Phase 37-01 (Issue #54): vehicle filter — OR-semantics within the
    // selection. Job matches when ANY of its assigned vehicle registrations
    // is in filters.vehicleRegistrations.
    if (hasVehicleFilter) {
      const jobRegs = vehicleRegsByJobId?.get(job.id);
      if (!jobRegs || !filters.vehicleRegistrations.some((r) => jobRegs.has(r))) return false;
    }

    // Text search (case-insensitive contains)
    if (hasSearch) {
      const fieldsToSearch = [job.ion, job.displayName, job.client, job.location];
      // Also search assigned staff names
      const staffNames = job.assignedStaff.map((a) => staffNameMap.get(a.staffId) ?? '');
      const allFields = [...fieldsToSearch, ...staffNames];
      const matches = allFields.some((f) => f && f.toLowerCase().includes(term));
      if (!matches) return false;
    }

    return true;
  });
}

const STATUS_LABELS: Record<StatusFilter, string> = {
  provisional: 'Provisional',
  confirmed: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  postponed: 'Postponed',
  cancelled: 'Cancelled',
};

// Tom-specified order in Issue #32:
const STATUS_ORDER: StatusFilter[] = [
  'provisional',
  'confirmed',
  'in_progress',
  'completed',
  'postponed',
  'cancelled',
];

const TYPE_LABELS: Record<TypeFilter, string> = {
  deal: 'Deal',
  ticket: 'Ticket',
};

const TYPE_ORDER: TypeFilter[] = ['deal', 'ticket'];

interface BoardFilterBarProps {
  jobs: Job[];
  staffList: ApiStaff[];
  // Phase 37-01 (Issue #54): active vehicles for the multi-select filter.
  vehicleList?: ApiVehicle[];
  filters: BoardFilters;
  onFiltersChange: (filters: BoardFilters) => void;
}

export function BoardFilterBar({
  jobs,
  staffList,
  vehicleList = [],
  filters,
  onFiltersChange,
}: BoardFilterBarProps) {
  const hasActiveFilter = !!(
    filters.staffId ||
    filters.client ||
    filters.status ||
    filters.type ||
    filters.vehicleRegistrations.length > 0 ||
    filters.searchTerm
  );

  // Derive unique staff who are assigned to at least one job
  const staffOptions = useMemo(() => {
    const assignedIds = new Set(jobs.flatMap((j) => j.assignedStaff.map((a) => a.staffId)));
    return staffList
      .filter((s) => assignedIds.has(s.id))
      .sort((a, b) => (a.known_as || a.name).localeCompare(b.known_as || b.name));
  }, [jobs, staffList]);

  // Derive unique clients — Issue #96 fix: COMPANY names only. The backend sets
  // client_name = the associated HubSpot company's name, but falls back to the
  // deal name when a deal has NO company; companyDomain is set ONLY when a
  // company is associated (and then client_name IS the company name). So list
  // clients only from jobs with a company association to drop deal-name fallbacks.
  const clientOptions = useMemo(() => {
    const clients = new Set(
      jobs
        .filter((j) => j.companyDomain)
        .map((j) => j.client)
        .filter(Boolean) as string[],
    );
    return [...clients].sort((a, b) => a.localeCompare(b));
  }, [jobs]);

  const update = (patch: Partial<BoardFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className="flex items-end gap-3 px-4 sm:px-6 py-2 bg-white border-t border-neutral-100 flex-wrap">
      {/* Search input */}
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] text-neutral-500 uppercase tracking-wide">Search</span>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            value={filters.searchTerm}
            onChange={(e) => update({ searchTerm: e.target.value })}
            placeholder="ION, job, staff…"
            className="text-[12px] border border-neutral-200 rounded pl-7 pr-7 py-1.5 bg-white text-neutral-700 min-w-[180px] focus:outline-none focus:ring-1 focus:ring-[#F26522]/30 focus:border-[#F26522]/50 placeholder:text-neutral-400"
          />
          {filters.searchTerm && (
            <button
              onClick={() => update({ searchTerm: '' })}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              <XIcon className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      <FilterSelect
        label="Staff"
        value={filters.staffId ?? ''}
        onChange={(v) => update({ staffId: v || null })}
        options={staffOptions.map((s) => ({ value: s.id, label: s.known_as || s.name }))}
      />
      <FilterSelect
        label="Client"
        value={filters.client ?? ''}
        onChange={(v) => update({ client: v || null })}
        options={clientOptions.map((c) => ({ value: c, label: c }))}
      />
      <FilterSelect
        label="Status"
        value={filters.status ?? ''}
        onChange={(v) => update({ status: (v as StatusFilter) || null })}
        options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] }))}
      />
      <FilterSelect
        label="Type"
        value={filters.type ?? ''}
        onChange={(v) => update({ type: (v as TypeFilter) || null })}
        options={TYPE_ORDER.map((t) => ({ value: t, label: TYPE_LABELS[t] }))}
      />
      <VehicleMultiSelect
        vehicles={vehicleList}
        selected={filters.vehicleRegistrations}
        onChange={(vehicleRegistrations) => update({ vehicleRegistrations })}
      />
      {hasActiveFilter && (
        <button
          onClick={() => onFiltersChange(EMPTY_FILTERS)}
          className="flex items-center gap-1 px-2 py-1.5 text-[11px] text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <XIcon className="w-3 h-3" />
          Clear filters
        </button>
      )}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-neutral-500 uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[12px] border border-neutral-200 rounded px-2 py-1.5 bg-white text-neutral-700 min-w-[120px] focus:outline-none focus:ring-1 focus:ring-[#F26522]/30 focus:border-[#F26522]/50"
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// Phase 37-01 (Issue #54): multi-select vehicle filter using Popover + checkbox
// list. Matches the height + visual rhythm of the other FilterSelect dropdowns.
function VehicleMultiSelect({
  vehicles,
  selected,
  onChange,
}: {
  vehicles: ApiVehicle[];
  selected: string[];
  onChange: (selected: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const sorted = useMemo(
    () => [...vehicles].sort((a, b) => a.registration.localeCompare(b.registration)),
    [vehicles],
  );
  const toggle = (reg: string) => {
    if (selected.includes(reg)) onChange(selected.filter((r) => r !== reg));
    else onChange([...selected, reg].sort());
  };
  const buttonLabel =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-neutral-500 uppercase tracking-wide">Vehicles</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 text-[12px] border border-neutral-200 rounded px-2 py-1.5 bg-white text-neutral-700 min-w-[120px] focus:outline-none focus:ring-1 focus:ring-[#F26522]/30 focus:border-[#F26522]/50 hover:border-neutral-300 transition-colors"
          >
            <Truck className="w-3 h-3 text-neutral-400" />
            <span className="truncate">{buttonLabel}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-56 max-h-72 overflow-y-auto" align="start">
          {sorted.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-neutral-400 italic">No active vehicles</div>
          ) : (
            <ul className="py-1">
              {sorted.map((v) => {
                const isSelected = selected.includes(v.registration);
                return (
                  <li key={v.id}>
                    <button
                      type="button"
                      onClick={() => toggle(v.registration)}
                      className="flex items-center gap-2 w-full px-3 py-1.5 text-[12px] text-left hover:bg-neutral-50 transition-colors"
                    >
                      <span className="inline-flex w-3.5 h-3.5 items-center justify-center border border-neutral-300 rounded shrink-0">
                        {isSelected && <Check className="w-3 h-3 text-[#F26522]" />}
                      </span>
                      <span className="text-neutral-700">{v.registration}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {selected.length > 0 && (
            <div className="border-t border-neutral-100 p-1.5">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full text-[11px] text-neutral-500 hover:text-neutral-700 py-1"
              >
                Clear selection
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
